import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { TenantUpdateStore } from "../../store";
import { EsignatureProvider } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputString } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { TenantTypeCreate } from "../../../tenantType/components/create/TenantTypeCreate";
import { TenantTypeCreateStore } from "../../../tenantType/store";
import { TenantStatusCreate } from "../../../tenantStatus/components/create/TenantStatusCreate";
import { TenantStatusCreateStore } from "../../../tenantStatus/store";
import { TenantPlanCreate } from "../../../tenantPlan/components/create/TenantPlanCreate";
import { TenantPlanCreateStore } from "../../../tenantPlan/store";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputDateTime } from "../../../common/controls";
import { LlmProviderCreate } from "../../../llmProvider/components/create/LlmProviderCreate";
import { LlmProviderCreateStore } from "../../../llmProvider/store";

export type TenantUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  eSignatureProviderOptions: { id: string; displayId: string }[];
  eSignatureProviderError?: string;
  eSignatureProvider: EsignatureProvider | "";
  onESignatureProviderChange?: (value: EsignatureProvider | "") => void;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  tenantTypeSelectedValue?: { displayId: string; id: string } | null;
  onTenantTypeChange?: (value: string) => void;
  onTenantTypeSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  tenantTypeOptions?: { displayId: string; id: string }[];
  tenantTypeError?: string;
  statusSelectedValue?: { displayId: string; id: string } | null;
  onStatusChange?: (value: string) => void;
  onStatusSelected?: (value: { displayId: string; id: string } | null) => void;
  statusOptions?: { displayId: string; id: string }[];
  statusError?: string;
  planSelectedValue?: { displayId: string; id: string } | null;
  onPlanChange?: (value: string) => void;
  onPlanSelected?: (value: { displayId: string; id: string } | null) => void;
  planOptions?: { displayId: string; id: string }[];
  planError?: string;
  logoOnRestoreFile: (id: string) => void;
  logoOnLoadFile: (file: any) => void;
  logoOnCommitFile: (changes: GridChangeSet) => void;
  logoFiles: FileCRUDType[];
  logoError?: string;
  publicUrl: string;
  publicUrlSelectedValue?: { displayId: string; id: string } | null;
  onPublicUrlChange?: (value: string) => void;
  publicUrlError?: string;
  idERP: string;
  idERPSelectedValue?: { displayId: string; id: string } | null;
  onIdERPChange?: (value: string) => void;
  idERPError?: string;
  idCRM: string;
  idCRMSelectedValue?: { displayId: string; id: string } | null;
  onIdCRMChange?: (value: string) => void;
  idCRMError?: string;
  monthlyTokenLimit: number | undefined;
  monthlyTokenLimitSelectedValue?: { displayId: string; id: string } | null;
  onMonthlyTokenLimitChange?: (value: number | undefined) => void;
  monthlyTokenLimitError?: string;
  currentMonthTokens: number;
  currentMonthTokensSelectedValue?: { displayId: string; id: string } | null;
  onCurrentMonthTokensChange?: (value: number) => void;
  currentMonthTokensError?: string;
  tokenLimitResetDay: number;
  tokenLimitResetDaySelectedValue?: { displayId: string; id: string } | null;
  onTokenLimitResetDayChange?: (value: number) => void;
  tokenLimitResetDayError?: string;
  lastTokenResetDate: Date | null;
  lastTokenResetDateSelectedValue?: { displayId: string; id: string } | null;
  onLastTokenResetDateChange?: (value: Date | null) => void;
  lastTokenResetDateError?: string;
  ragLlmProviderSelectedValue?: { displayId: string; id: string } | null;
  onRagLlmProviderChange?: (value: string) => void;
  onRagLlmProviderSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  ragLlmProviderOptions?: { displayId: string; id: string }[];
  ragLlmProviderError?: string;
  ragLlmModel: string;
  ragLlmModelSelectedValue?: { displayId: string; id: string } | null;
  onRagLlmModelChange?: (value: string) => void;
  ragLlmModelError?: string;
  currentStorageBytes: number;
  currentStorageBytesSelectedValue?: { displayId: string; id: string } | null;
  onCurrentStorageBytesChange?: (value: number) => void;
  currentStorageBytesError?: string;
  storageByteLimit: number | undefined;
  storageByteLimitSelectedValue?: { displayId: string; id: string } | null;
  onStorageByteLimitChange?: (value: number | undefined) => void;
  storageByteLimitError?: string;
  dummyCRUDRestoredObservable: number;
  tenantUpdateStore: TenantUpdateStore;
};

export const TenantUpdateForm: React.FC<TenantUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    eSignatureProviderOptions = [],
    eSignatureProviderError,
    eSignatureProvider = "" as EsignatureProvider | "",
    onESignatureProviderChange = () => {},
    name = "",
    nameError,
    onNameChange = () => {},
    onTenantTypeSelected = () => {},
    tenantTypeOptions = [],
    tenantTypeSelectedValue = null,
    tenantTypeError,
    onTenantTypeChange = () => {},
    onStatusSelected = () => {},
    statusOptions = [],
    statusSelectedValue = null,
    statusError,
    onStatusChange = () => {},
    onPlanSelected = () => {},
    planOptions = [],
    planSelectedValue = null,
    planError,
    onPlanChange = () => {},
    logoOnRestoreFile,
    logoOnLoadFile,
    logoOnCommitFile,
    logoFiles,
    logoError,
    publicUrl = "",
    publicUrlError,
    onPublicUrlChange = () => {},
    idERP = "",
    idERPError,
    onIdERPChange = () => {},
    idCRM = "",
    idCRMError,
    onIdCRMChange = () => {},
    monthlyTokenLimit = undefined,
    monthlyTokenLimitError,
    onMonthlyTokenLimitChange = () => {},
    currentMonthTokens = 0,
    currentMonthTokensError,
    onCurrentMonthTokensChange = () => {},
    tokenLimitResetDay = 0,
    tokenLimitResetDayError,
    onTokenLimitResetDayChange = () => {},
    lastTokenResetDate = null,
    lastTokenResetDateError,
    onLastTokenResetDateChange = () => {},
    onRagLlmProviderSelected = () => {},
    ragLlmProviderOptions = [],
    ragLlmProviderSelectedValue = null,
    ragLlmProviderError,
    onRagLlmProviderChange = () => {},
    ragLlmModel = "",
    ragLlmModelError,
    onRagLlmModelChange = () => {},
    currentStorageBytes = 0,
    currentStorageBytesError,
    onCurrentStorageBytesChange = () => {},
    storageByteLimit = undefined,
    storageByteLimitError,
    onStorageByteLimitChange = () => {},
    dummyCRUDRestoredObservable,
    tenantUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const getViewAction = (
      onDownload: (props: {
        id: string;
        type: string;
        name: string;
        __status__: RowCrudStatus;
      }) => void,
      onViewPdf?: (props: any) => void
    ) => {
      const actions: any[] = [];
      if (onViewPdf) {
        actions.push({
          columnName: "__action_view_pdf",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
          icon: <VisibilityIcon />,
          label: langStore.viewCommand,
          isActionEnabled: (props: any) =>
            props.name?.toLowerCase().endsWith(".pdf"),
        });
      }
      actions.push({
        columnName: "__action_view",
        onClick: (props: any) =>
          isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
        icon: <DownloadIcon />,
        label: langStore.downloadCommand,
      });
      return actions;
    };
    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="Tenant"
            mode="update"
            values={{}}
            options={{
              tenantType: tenantTypeOptions,
              status: statusOptions,
              plan: planOptions,
              ragLlmProvider: ragLlmProviderOptions,
            }}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "eSignatureProvider":
                  onESignatureProviderChange?.(valor);
                  break;
                case "name":
                  onNameChange?.(valor);
                  break;
                case "tenantType":
                  onTenantTypeSelected?.(valor);
                  break;
                case "status":
                  onStatusSelected?.(valor);
                  break;
                case "plan":
                  onPlanSelected?.(valor);
                  break;
                case "publicUrl":
                  onPublicUrlChange?.(valor);
                  break;
                case "idERP":
                  onIdERPChange?.(valor);
                  break;
                case "idCRM":
                  onIdCRMChange?.(valor);
                  break;
                case "monthlyTokenLimit":
                  onMonthlyTokenLimitChange?.(valor);
                  break;
                case "currentMonthTokens":
                  onCurrentMonthTokensChange?.(valor);
                  break;
                case "tokenLimitResetDay":
                  onTokenLimitResetDayChange?.(valor);
                  break;
                case "lastTokenResetDate":
                  onLastTokenResetDateChange?.(valor);
                  break;
                case "ragLlmProvider":
                  onRagLlmProviderSelected?.(valor);
                  break;
                case "ragLlmModel":
                  onRagLlmModelChange?.(valor);
                  break;
                case "currentStorageBytes":
                  onCurrentStorageBytesChange?.(valor);
                  break;
                case "storageByteLimit":
                  onStorageByteLimitChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="null"
            errorText={tenantUpdateStore.getCardErrors("null")}
            title={langStore.groupTenantLabel}
            collapsible={true}
          >
            <FormControl fullWidth>
              <InputReference
                id="eSignatureProvider"
                autoFocus
                disabled={false}
                error={Boolean(eSignatureProviderError)}
                helperText={
                  eSignatureProviderError ||
                  tenantUpdateStore.warnings["eSignatureProvider"]?.message
                }
                label={langStore.TenantESignatureProviderLabel}
                options={eSignatureProviderOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  eSignatureProvider
                    ? {
                        id: eSignatureProvider,
                        displayId:
                          eSignatureProviderOptions.find(
                            (o: { id: string }) => o.id === eSignatureProvider
                          )?.displayId || eSignatureProvider,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onESignatureProviderChange(
                    (value?.id as EsignatureProvider | "") ||
                      ("" as EsignatureProvider | "")
                  )
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="tenant"
            errorText={tenantUpdateStore.getCardErrors("tenant")}
            title={langStore.groupTenantTenantLabel}
            collapsible={true}
            expanded={tenantUpdateStore.getCardState("tenant")}
            toggleExpand={() => tenantUpdateStore.toggleCardState("tenant")}
          >
            <FormControl fullWidth>
              <InputString
                value={name}
                error={Boolean(nameError)}
                warning={
                  !nameError && Boolean(tenantUpdateStore.warnings["name"])
                }
                helperText={
                  nameError || tenantUpdateStore.warnings["name"]?.message
                }
                label={langStore.TenantNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.TenantNameDescription}
              />
              <InputReference
                id="tenantType"
                label={langStore.TenantTenantTypeLabel}
                error={Boolean(tenantTypeError)}
                helperText={
                  tenantTypeError ||
                  tenantUpdateStore.warnings["tenantType"]?.message
                }
                selectedValue={tenantTypeSelectedValue}
                onChange={(value: string) => onTenantTypeChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onTenantTypeSelected(value)
                }
                options={tenantTypeOptions}
                description={langStore.TenantTenantTypeDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantType_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantTypeCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantTypeCreate
                        tenantTypeCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantType"
                    cancelMethod="cancelNewTenantType"
                  />
                )}
              />
              <InputReference
                id="status"
                label={langStore.TenantStatusLabel}
                error={Boolean(statusError)}
                helperText={
                  statusError || tenantUpdateStore.warnings["status"]?.message
                }
                selectedValue={statusSelectedValue}
                onChange={(value: string) => onStatusChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onStatusSelected(value)
                }
                options={statusOptions}
                description={langStore.TenantStatusDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantStatus_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantStatusCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantStatusCreate
                        tenantStatusCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantStatus"
                    cancelMethod="cancelNewTenantStatus"
                  />
                )}
              />
              <InputReference
                id="plan"
                label={langStore.TenantPlanLabel}
                error={Boolean(planError)}
                helperText={
                  planError || tenantUpdateStore.warnings["plan"]?.message
                }
                selectedValue={planSelectedValue}
                onChange={(value: string) => onPlanChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onPlanSelected(value)
                }
                options={planOptions}
                description={langStore.TenantPlanDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "tenantPlan_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantPlanCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantPlanCreate
                        tenantPlanCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenantPlan"
                    cancelMethod="cancelNewTenantPlan"
                  />
                )}
              />
              <LoadFilesCRUDCompact
                rows={logoFiles}
                maxRows={1}
                menuActions={getViewAction(
                  (props) => tenantUpdateStore.fileDownload(props, "logo"),
                  (props) => tenantUpdateStore.viewFilePdf(props, "logo")
                )}
                showAddCommand={true}
                showDeleteCommand={true}
                label={langStore.TenantLogoLabel}
                description={langStore.TenantLogoDescription}
                onLoadFile={(file: File) => logoOnLoadFile(file)}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
                onRestore={(id: string) => logoOnRestoreFile(id)}
                onCommitChanges={(changes: GridChangeSet) =>
                  logoOnCommitFile(changes)
                }
                dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                error={logoError}
              />
              <InputString
                value={publicUrl}
                error={Boolean(publicUrlError)}
                warning={
                  !publicUrlError &&
                  Boolean(tenantUpdateStore.warnings["publicUrl"])
                }
                helperText={
                  publicUrlError ||
                  tenantUpdateStore.warnings["publicUrl"]?.message
                }
                label={langStore.TenantPublicUrlLabel}
                onChange={(value: string) => onPublicUrlChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantPublicUrlDescription}
              />
              <InputString
                value={idERP}
                error={Boolean(idERPError)}
                warning={
                  !idERPError && Boolean(tenantUpdateStore.warnings["idERP"])
                }
                helperText={
                  idERPError || tenantUpdateStore.warnings["idERP"]?.message
                }
                label={langStore.TenantIdERPLabel}
                onChange={(value: string) => onIdERPChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantIdERPDescription}
              />
              <InputString
                value={idCRM}
                error={Boolean(idCRMError)}
                warning={
                  !idCRMError && Boolean(tenantUpdateStore.warnings["idCRM"])
                }
                helperText={
                  idCRMError || tenantUpdateStore.warnings["idCRM"]?.message
                }
                label={langStore.TenantIdCRMLabel}
                onChange={(value: string) => onIdCRMChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantIdCRMDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="llmLimits"
            errorText={tenantUpdateStore.getCardErrors("llmLimits")}
            title={langStore.groupTenantLlmLimitsLabel}
            collapsible={true}
            expanded={tenantUpdateStore.getCardState("llmLimits")}
            toggleExpand={() => tenantUpdateStore.toggleCardState("llmLimits")}
          >
            <FormControl fullWidth>
              <InputNumber
                value={monthlyTokenLimit}
                nullable={true}
                error={Boolean(monthlyTokenLimitError)}
                warning={
                  !monthlyTokenLimitError &&
                  Boolean(tenantUpdateStore.warnings["monthlyTokenLimit"])
                }
                helperText={
                  monthlyTokenLimitError ||
                  tenantUpdateStore.warnings["monthlyTokenLimit"]?.message
                }
                label={langStore.TenantMonthlyTokenLimitLabel}
                decimalScale={0}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantMonthlyTokenLimitDescription}
                onChange={(value: number | undefined) =>
                  onMonthlyTokenLimitChange(value)
                }
              />
              <InputNumber
                value={currentMonthTokens}
                error={Boolean(currentMonthTokensError)}
                warning={
                  !currentMonthTokensError &&
                  Boolean(tenantUpdateStore.warnings["currentMonthTokens"])
                }
                helperText={
                  currentMonthTokensError ||
                  tenantUpdateStore.warnings["currentMonthTokens"]?.message
                }
                label={langStore.TenantCurrentMonthTokensLabel}
                decimalScale={0}
                description={langStore.TenantCurrentMonthTokensDescription}
                onChange={(value: number) => onCurrentMonthTokensChange(value)}
              />
              <InputNumber
                value={tokenLimitResetDay}
                error={Boolean(tokenLimitResetDayError)}
                warning={
                  !tokenLimitResetDayError &&
                  Boolean(tenantUpdateStore.warnings["tokenLimitResetDay"])
                }
                helperText={
                  tokenLimitResetDayError ||
                  tenantUpdateStore.warnings["tokenLimitResetDay"]?.message
                }
                label={langStore.TenantTokenLimitResetDayLabel}
                decimalScale={0}
                description={langStore.TenantTokenLimitResetDayDescription}
                onChange={(value: number) => onTokenLimitResetDayChange(value)}
              />
              <InputDateTime
                value={lastTokenResetDate}
                error={Boolean(lastTokenResetDateError)}
                warning={
                  !lastTokenResetDateError &&
                  Boolean(tenantUpdateStore.warnings["lastTokenResetDate"])
                }
                helperText={
                  lastTokenResetDateError ||
                  tenantUpdateStore.warnings["lastTokenResetDate"]?.message
                }
                label={langStore.TenantLastTokenResetDateLabel}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantLastTokenResetDateDescription}
                onChange={(value: Date | null | null) =>
                  onLastTokenResetDateChange(value)
                }
              />
              <InputReference
                id="ragLlmProvider"
                label={langStore.TenantRagLlmProviderLabel}
                error={Boolean(ragLlmProviderError)}
                helperText={
                  ragLlmProviderError ||
                  tenantUpdateStore.warnings["ragLlmProvider"]?.message
                }
                selectedValue={ragLlmProviderSelectedValue}
                onChange={(value: string) => onRagLlmProviderChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onRagLlmProviderSelected(value)
                }
                options={ragLlmProviderOptions}
                description={langStore.TenantRagLlmProviderDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions(
                  "llmProvider_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new LlmProviderCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <LlmProviderCreate
                        llmProviderCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewLlmProvider"
                    cancelMethod="cancelNewLlmProvider"
                  />
                )}
              />
              <InputString
                value={ragLlmModel}
                error={Boolean(ragLlmModelError)}
                warning={
                  !ragLlmModelError &&
                  Boolean(tenantUpdateStore.warnings["ragLlmModel"])
                }
                helperText={
                  ragLlmModelError ||
                  tenantUpdateStore.warnings["ragLlmModel"]?.message
                }
                label={langStore.TenantRagLlmModelLabel}
                onChange={(value: string) => onRagLlmModelChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantRagLlmModelDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="storageLimits"
            errorText={tenantUpdateStore.getCardErrors("storageLimits")}
            title={langStore.groupTenantStorageLimitsLabel}
            collapsible={true}
            expanded={tenantUpdateStore.getCardState("storageLimits")}
            toggleExpand={() =>
              tenantUpdateStore.toggleCardState("storageLimits")
            }
          >
            <FormControl fullWidth>
              <InputNumber
                value={currentStorageBytes}
                error={Boolean(currentStorageBytesError)}
                warning={
                  !currentStorageBytesError &&
                  Boolean(tenantUpdateStore.warnings["currentStorageBytes"])
                }
                helperText={
                  currentStorageBytesError ||
                  tenantUpdateStore.warnings["currentStorageBytes"]?.message
                }
                label={langStore.TenantCurrentStorageBytesLabel}
                decimalScale={0}
                description={langStore.TenantCurrentStorageBytesDescription}
                onChange={(value: number) => onCurrentStorageBytesChange(value)}
              />
              <InputNumber
                value={storageByteLimit}
                nullable={true}
                error={Boolean(storageByteLimitError)}
                warning={
                  !storageByteLimitError &&
                  Boolean(tenantUpdateStore.warnings["storageByteLimit"])
                }
                helperText={
                  storageByteLimitError ||
                  tenantUpdateStore.warnings["storageByteLimit"]?.message
                }
                label={langStore.TenantStorageByteLimitLabel}
                decimalScale={0}
                nullText={langStore.optionalFieldHint}
                description={langStore.TenantStorageByteLimitDescription}
                onChange={(value: number | undefined) =>
                  onStorageByteLimitChange(value)
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
        <PDFViewerDialog
          open={tenantUpdateStore.pdfViewerOpen}
          onClose={() => tenantUpdateStore.closePdfViewer()}
          pdfUrl={tenantUpdateStore.pdfViewerUrl}
          title={tenantUpdateStore.pdfViewerTitle}
        />
      </Grid>
    );
  }
);
